import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  CheckboxProps,
  GlobalStyles,
  IconButton,
  SxProps,
  Theme,
  ThemeOptions,
  ThemeProvider,
  createTheme,
  useTheme,
} from "@mui/material";
import React from "react";

import { dashboardDensityMediaQuery } from "../../common/dashboard/DashboardDensityStyles";


export const invoiceDesignTokens = {
  blue: "#0091FF",
  border: "#D7DBE0",
  closeRed: "#F21B1B",
  dark: "#26282C",
  disabled: "#EDEDED",
  gold: "#E1A731",
  goldDark: "#C6912A",
  green: "#12B981",
  ink: "#0A0A0A",
  modalBorder: "#ECECEC",
  modalShadow: "0 18px 48px rgba(0,0,0,0.18)",
} as const;

export const invoiceDesignFontFamily = 'var(--cmp-design-font-family, "DM Sans", sans-serif)';

// Claude's invoice export uses a 16 px root at the matched 1920 x 1080
// viewport. The general dashboard applies a 15 px density scale there. Keep
// that general behavior for other modules, but restore Claude's exact scale
// while one of the invoice routes is mounted. GlobalStyles removes this rule
// automatically when the route unmounts.
export const invoiceDesignDensityOverride = {
  [dashboardDensityMediaQuery]: {
    ":root": {
      fontSize: "16px !important",
    },
  },
} as const;

const invoiceDesignThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: invoiceDesignFontFamily,
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          minHeight: 42,
          paddingBottom: "2px !important",
          paddingTop: "2px !important",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: invoiceDesignFontFamily,
          fontSize: 14,
          fontWeight: 600,
          textTransform: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          fontFamily: invoiceDesignFontFamily,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: 8,
          fontFamily: invoiceDesignFontFamily,
          fontSize: 14,
          "&:not(.MuiInputBase-multiline)": {
            minHeight: 42,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#9E9E9E",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6F6F6F",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: invoiceDesignTokens.gold,
            borderWidth: 1,
          },
        },
        input: {
          boxSizing: "border-box",
          height: "auto",
          padding: "10px 12px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: invoiceDesignFontFamily,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
  },
};

export const invoiceFieldSx = {
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    fontFamily: invoiceDesignFontFamily,
    fontSize: 14,
    minHeight: 42,
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    padding: "10px 12px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8A8A8A",
    opacity: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9E9E9E",
  },
} as const;

// Attendee menus use the rendered design's text colours. Shared filters keep
// the menu defaults unless they select these styles.
export const invoiceAttendeeMenuSx = {
  "& .MuiMenuItem-root:not(.Mui-disabled), & .MuiInputBase-root": {
    color: "#000000",
    letterSpacing: "normal",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9E9E9E",
    opacity: 1,
  },
} as const;

export const invoiceAttendeeModalMenuSx = {
  ...invoiceAttendeeMenuSx,
  "& .MuiMenuItem-root:not(.Mui-disabled)": {
    color: "#0A0A0A",
    letterSpacing: "normal",
    lineHeight: "normal !important",
  },
} as const;

// An empty attendee result is informational. Keep its disabled semantics
// without MUI's disabled opacity, which fades the design's muted text twice.
export const invoiceAttendeeNoMatchesSx = {
  color: "#727272",
  fontSize: 14,
  fontWeight: 400,
  letterSpacing: "normal",
  lineHeight: "normal",
  minHeight: 0,
  px: "12px",
  py: "12px",
  whiteSpace: "normal",
  "&.Mui-disabled": { opacity: 1 },
} as const;

// Process Payment uses the compact 38 px fields from the rendered Claude
// modal. Keep this separate from the 42 px page-field system so the override
// is limited to the generic and event payment adapters.
export const invoiceProcessPaymentFieldSx = {
  ...invoiceFieldSx,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: "38px !important",
    minHeight: "38px !important",
  },
  "& .MuiInputBase-input": {
    boxSizing: "border-box",
    height: "36px !important",
    lineHeight: "18px",
    padding: "9px 12px",
  },
  "& .MuiInputBase-root.MuiInputBase-multiline": {
    height: "56px !important",
    minHeight: "56px !important",
    padding: 0,
  },
  "& .MuiInputBase-inputMultiline": {
    boxSizing: "border-box",
    height: "54px !important",
    lineHeight: "18px",
    minHeight: "54px !important",
    padding: "9px 12px !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: invoiceDesignTokens.border,
  },
} as const;

// The checkbox look comes from cmpTheme (Monday 13396402468); invoice tables
// keep the 18px box with no padding.
export const InvoiceDesignCheckbox = ({
  sx,
  ...checkboxProps
}: CheckboxProps): React.ReactElement => (
  <Checkbox
    {...checkboxProps}
    sx={[
      {
        height: 18,
        padding: 0,
        width: 18,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  />
);

export const invoiceDialogPaperSx = ({
  maxWidth,
}: {
  maxWidth: number;
}): SxProps<Theme> => ({
  border: 0,
  borderRadius: "14px",
  boxShadow: invoiceDesignTokens.modalShadow,
  fontFamily: invoiceDesignFontFamily,
  margin: { xs: "14px", sm: "24px" },
  maxHeight: "92vh",
  maxWidth,
  overflow: "hidden",
  width: "100%",
});

export const invoiceModalActionSx = ({
  backgroundColor,
  hoverBackgroundColor,
}: {
  backgroundColor: string;
  hoverBackgroundColor: string;
}) => ({
  backgroundColor,
  borderRadius: "8px",
  color: "white",
  fontFamily: invoiceDesignFontFamily,
  fontSize: 14,
  fontWeight: 600,
  minHeight: 42,
  px: 2.5,
  textTransform: "none",
  "&:hover": { backgroundColor: hoverBackgroundColor },
});

export const invoiceOutlinedActionSx = {
  backgroundColor: "#FFFFFF",
  border: `1px solid ${invoiceDesignTokens.ink}`,
  borderRadius: "8px",
  color: invoiceDesignTokens.ink,
  fontFamily: invoiceDesignFontFamily,
  fontSize: 14,
  fontWeight: 600,
  minHeight: 45,
  px: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#F7F7F8",
    border: `1px solid ${invoiceDesignTokens.ink}`,
  },
} as const;

export const InvoiceDesignScope = ({
  children,
  fontFamily,
}: Readonly<{
  children: React.ReactNode;
  fontFamily?: string;
}>): React.ReactElement => {
  const parentTheme = useTheme();
  const scopedTheme = React.useMemo(
    () => createTheme(parentTheme, invoiceDesignThemeOptions),
    [parentTheme],
  );

  return (
    <ThemeProvider theme={scopedTheme}>
      <GlobalStyles styles={invoiceDesignDensityOverride} />
      {/* Portaled menus and the page header share the route's font override.
          Unmounting this scope restores the existing font for other routes. */}
      {fontFamily && <GlobalStyles styles={{ ":root": { "--cmp-design-font-family": fontFamily } }} />}
      <div style={{ fontFamily: invoiceDesignFontFamily }}>{children}</div>
    </ThemeProvider>
  );
};

export const InvoiceDialogCloseButton = ({
  ariaLabel,
  disabled = false,
  hoverBackgroundColor = "#D90F1A",
  onClick,
  outlinedIcon,
}: Readonly<{
  ariaLabel: string;
  disabled?: boolean;
  hoverBackgroundColor?: string;
  onClick: () => void;
  outlinedIcon?: Readonly<{ size: number; strokeWidth: number }>;
}>): React.ReactElement => (
  <IconButton
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onClick}
    sx={{
      backgroundColor: invoiceDesignTokens.closeRed,
      color: "white",
      height: 28,
      position: "absolute",
      right: 14,
      top: 14,
      width: 28,
      zIndex: 2,
      "&:hover": { backgroundColor: hoverBackgroundColor },
      "&.Mui-disabled": {
        backgroundColor: "#F9A2A7",
        color: "white",
      },
    }}
  >
    {outlinedIcon ? (
      <svg
        aria-hidden="true"
        width={outlinedIcon.size}
        height={outlinedIcon.size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={outlinedIcon.strokeWidth}
        strokeLinecap="round"
        style={{ flexShrink: 0 }}
      >
        <path d="m6 6 12 12M18 6 6 18" />
      </svg>
    ) : (
      <CloseIcon sx={{ fontSize: 16 }} />
    )}
  </IconButton>
);
